import type { PracticeSchedule } from "../../types";

/* Editor for the recurring practice cadence. Controlled: the parent owns the
   value and the save/export actions. Framed as encouragement — it sets a rhythm
   and a reminder, it never locks anything. */

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"]; // 0=Mon … 6=Sun

const LEAD_OPTIONS = [
  { v: 0, label: "At start" },
  { v: 10, label: "10 min before" },
  { v: 30, label: "30 min before" },
  { v: 60, label: "1 hour before" },
];

interface Props {
  value: PracticeSchedule;
  onChange: (next: PracticeSchedule) => void;
  onSave: () => void;
  onExport: () => void;
  saving: boolean;
  dirty: boolean;
}

function Switch({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${on ? "bg-success" : "bg-surface2"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : ""}`} />
    </button>
  );
}

export default function ScheduleEditor({ value, onChange, onSave, onExport, saving, dirty }: Props) {
  const toggleDay = (d: number) =>
    onChange({ ...value, days: value.days.includes(d) ? value.days.filter((x) => x !== d) : [...value.days, d].sort((a, b) => a - b) });

  const canExport = value.days.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="font-display font-semibold text-[1.05rem] text-ink">Your rhythm</h3>
        <p className="text-[0.8rem] text-text-tertiary mt-0.5 leading-snug">A little, often. Pick the days that fit your week — that's your plan.</p>
      </div>

      {/* day picker */}
      <div>
        <span className="section-label">Practice days</span>
        <div className="flex gap-1.5 mt-2.5">
          {DAY_LABELS.map((label, d) => {
            const on = value.days.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(d)}
                aria-pressed={on}
                className={`flex-1 aspect-square rounded-[0.6rem] text-[0.8rem] font-semibold transition-colors cursor-pointer ${
                  on ? "bg-success text-white" : "bg-surface2/50 text-text-secondary hover:text-ink"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* time */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-[0.85rem] text-text">Time of day</span>
        <input
          type="time"
          value={value.time}
          onChange={(e) => onChange({ ...value, time: e.target.value || "18:00" })}
          className="input-base px-3 py-1.5 text-base sm:text-[0.85rem] w-32 tabular-nums"
        />
      </div>

      {/* reminders */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[0.85rem] text-text">Calendar reminder</span>
          <Switch on={value.reminders} onClick={() => onChange({ ...value, reminders: !value.reminders })} label="Include a reminder alarm" />
        </div>
        {value.reminders && (
          <div className="flex flex-wrap gap-1.5">
            {LEAD_OPTIONS.map((o) => {
              const on = value.reminder_minutes_before === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => onChange({ ...value, reminder_minutes_before: o.v })}
                  aria-pressed={on}
                  className={`px-3 py-1.5 rounded-full text-[0.75rem] font-medium border transition-colors cursor-pointer ${
                    on ? "border-success text-success bg-success/10" : "border-border-soft text-text-tertiary hover:text-ink"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* actions */}
      <div className="flex flex-col gap-2 pt-1">
        <button type="button" onClick={onSave} disabled={saving || !dirty} className="btn-primary px-4 py-2.5 text-[0.85rem] disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? "Saving…" : dirty ? "Save plan" : "Saved"}
        </button>
        <button
          type="button"
          onClick={onExport}
          disabled={!canExport}
          className="btn-quiet px-4 py-2.5 text-[0.85rem] inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Add to calendar (.ics)
        </button>
        <p className="text-[0.72rem] text-text-tertiary text-center leading-snug">
          Downloads a calendar file you can open in Apple Calendar, Google Calendar or Outlook. Your calendar handles the reminders, so you stay in control.
        </p>
      </div>
    </div>
  );
}
